import { ReactNode } from "react";
import styled, { css, FlattenSimpleInterpolation } from "styled-components";
import { alpha, numeric } from "../typography";
import { SmallPlainIcon } from "./Icon";

type BaseDatePickerProps = {
  label?: string;
  id?: string;
  isRequired?: boolean;
  errorMessage?: string;
  disabled?: boolean;
  children: ReactNode;
};

export const createIcon = (
  disabled: boolean,
  readOnly: boolean,
  focused: boolean
) => {
  return (
    <SmallPlainIcon
      iconName="calendar"
      variant={
        disabled || readOnly ? "DISABLED" : focused ? "ACTIVE" : "DEFAULT"
      }
    />
  );
};
export const BaseDatePicker = (props: BaseDatePickerProps) => {
  return (
    <DatePickerLabel>
      <label htmlFor={props.id}>
        {props.label && (
          <LabelText
            isDisabled={!!props.disabled}
            hasError={!!props.errorMessage}
          >
            {props.label} {props.isRequired ? <Asterisk>*</Asterisk> : null}
          </LabelText>
        )}
      </label>
      {props.children}
    </DatePickerLabel>
  );
};
type LabelTextProps = {
  isDisabled?: boolean;
  hasError: boolean;
  isRequired?: boolean;
  isReadOnly?: boolean;
};

export const LabelText = styled.div<LabelTextProps>`
  margin-bottom: 4px;
  ${alpha.primaryActive.body3}
  ${(props) => {
    if (props.isDisabled) {
      return alpha.primaryInactive.body3;
    }

    if (props.hasError) {
      return alpha.error.body3;
    }

    if (props.isReadOnly) {
      return css`
        display: flex;
        align-items: center;
        gap: 5px;
      `;
    }
    return "";
  }}
`;

export const Asterisk = styled.span`
  ${alpha.error.body3};
`;

export const Caption = styled.span<LabelTextProps>`
  ${(props) =>
    props.hasError
      ? alpha.error.caption
      : props.isDisabled
      ? alpha.primaryInactive.caption
      : alpha.primaryActive.caption}
`;

export const NavigationButton = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 2px;
  background-color: transparent;

  &:hover {
    background-color: var(--color-secondary-100);
    border-radius: 50%;
    transition: border-radius 130ms ease-out;

    svg {
      --main-color: var(--color-secondary-600);
    }
  }
`;

const DatePickerLabel = styled.span`
  display: flex;
  flex-direction: column;
`;

type StyledDatePickerContainerProps = {
  disabled?: boolean;
  readOnly?: boolean;
  hasError?: boolean;
  hasSelectors?: boolean;
  styles?: FlattenSimpleInterpolation;
};

export const StyledDatePickerContainer = styled.div<StyledDatePickerContainerProps>`
  width: 100%;
  height: 30px;
  border: 1px solid var(--color-primary-400);
  border-radius: var(--size-border-radius-small);
  :focus-within {
    outline: none;
  }

  .CalendarMonth {
    ${numeric.primaryActive.caption}
    letter-spacing: inherit;
  }

  .DayPicker_weekHeader {
    top: 54px;
  }

  strong {
    ${alpha.primaryActive.subtitle3}
    letter-spacing: inherit;
    color: rgb(85, 91, 94);
  }

  small {
    ${alpha.primaryActive.body3}
    color: var(--color-primary-400);
  }

  .CalendarMonth_caption {
    padding-top: 16px;
    padding-bottom: 42px;
  }

  .DateInput_input {
    box-sizing: content-box;
    ${numeric.primaryActive.body4}
    letter-spacing: inherit;
    color: var(--color-primary-400);
    height: 32px;
  }

  .DateInput_input:focus {
    border: 0;
    background: transparent;
  }

  .SingleDatePickerInput__block {
    background: transparent;
    display: flex;
    justify-content: space-between;
    height: 32px;
  }

  .DateRangePickerInput__block {
    background: transparent;
    display: flex;
    width: 100%;
    justify-content: space-between;
    height: 32px;
  }

  .SingleDatePickerInput_calendarIcon {
    padding: 0px 4px 0px 5px;
    height: 32px;
    text-align: right;
    display: flex;
    justify-content: flex-end;
    align-items: center;
  }

  .DateRangePickerInput {
    height: 32px;
    color: rgb(148, 163, 175);
    background: transparent;
  }

  .DateRangePickerInput_arrow {
    padding: 5px 0px 0px 0px;
    margin: 0px;
  }
  .DateRangePickerInput_calendarIcon {
    padding: 0px 4px 0px 5px;
    margin-left: auto;
    text-align: right;
    width: 100%;
    display: flex;
    justify-content: flex-end;
    align-items: center;
  }

  .DateInput__small {
    height: 32px;
    background: transparent;
  }
  .DateInput_input__small {
    height: 32px;
    width: inherit;
    background: transparent;
    padding: 0px 0px 0px 10px;
  }
  .DateInput_input__focused {
    border-bottom: 0;
    background: transparent;
  }
  .DateInput_fang {
    height: 0;
    visibility: hidden;
  }

  .CalendarDay {
    box-sizing: border-box;
    cursor: pointer;
    font-size: var(--size-font-base);
    text-align: center;
  }
  .CalendarDay__default {
    background: var(--color-neutral-000-full);
    border: 4px transparent;
  }
  .CalendarDay__default:hover {
    background-color: var(--color-secondary-800);
    color: var(--color-neutral-000-full);
    border-width: 0px;
    border-radius: 50%;
    border-color: transparent;
  }
  .CalendarDay__selected_span {
    color: var(--color-neutral-800);
    border-radius: 0;
    background-color: var(--color-secondary-300);
  }

  .CalendarDay__selected {
    background-color: var(--color-secondary-800);
    color: var(--color-neutral-000-full);
    border-radius: 50%;
  }

  .CalendarDay__selected:hover {
    cursor: pointer;
  }

  .CalendarDay__hovered_span {
    color: var(--color-neutral-800);
    background-color: var(--color-secondary-300);
  }

  .CalendarDay__hovered_span:hover {
    border-color: transparent;
    color: var(--color-neutral-000-full);
  }

  .DayPickerNavigation_button__horizontalDefault {
    position: absolute;
    top: 13px;
    line-height: 0.78;
    border-width: 5px;
    border-color: var(--color-neutral-000-full);

    padding: 6px 9px;
  }
  .DayPickerNavigation_button__horizontal {
    position: absolute;
    top: 10px;
    left: 0;
    right: 0;
  }
  .DayPickerNavigation_button__horizontal span {
    position: absolute;
  }

  .DateRangePicker_picker {
    z-index: 999999;
  }

  .DateRangePicker_picker .DayPickerNavigation_button__horizontal .nav-prev {
    left: 22px;
    top: -3px;
  }
  .SingleDatePicker_picker .DayPickerNavigation_button__horizontal .nav-prev {
    left: 16px;
    top: -3px;
  }

  .DateRangePicker_picker .DayPickerNavigation_button__horizontal .nav-next {
    right: 22px;
    top: -3px;
  }

  .SingleDatePicker_picker .DayPickerNavigation_button__horizontal .nav-next {
    right: 16px;
    top: -3px;
  }

  .DayPickerNavigation_button:hover {
    border-width: 1px;
    border-left: 0;
    border-right: 0;
    border-top: 0;
    border-bottom: 0;
  }
  .DayPickerNavigation_button__default {
    border: 1px transparent;
    padding: 10px 0px;
  }
  .DayPickerNavigation_button__default:hover {
    border-width: 1px;
    border-left: 0;
    border-right: 0;
    border-top: 0;
    border-bottom: 0;
  }

  .DayPickerNavigation_button__horizontalDefault:hover {
    border-width: 1px;
  }

  ${(props) =>
    props.hasError
      ? css`
          border: 1px solid var(--color-error-500);
        `
      : props.disabled || props.readOnly
      ? css`
          border: 1px solid var(--color-primary-200);
          input::placeholder {
            font-style: normal;
            color: var(--color-primary-200);
          }
        `
      : css`
          &:hover {
            border: 1px solid var(--color-primary-800);
          }
          &:focus-within {
            border: 1px solid var(--color-secondary-800);
          }
        `}

  ${(props) =>
    props.hasSelectors &&
    css`
      .DayPicker_weekHeader {
        top: 74px;
      }

      .DayPickerNavigation_button__horizontal .nav-prev,
      .DayPickerNavigation_button__horizontal .nav-next {
        top: 4px !important;
      }

      .DateRangePicker_picker
        .DayPickerNavigation_button__horizontal
        .nav-prev {
        left: 9px;
      }

      .DateRangePicker_picker
        .DayPickerNavigation_button__horizontal
        .nav-next {
        right: 11px;
      }

      .SingleDatePicker_picker
        .DayPickerNavigation_button__horizontal
        .nav-prev {
        left: 8px;
      }

      .SingleDatePicker_picker
        .DayPickerNavigation_button__horizontal
        .nav-next {
        right: 9px;
      }
    `}


  .react-datepicker__input-container {
    width: inherit;
  }

  .react-datepicker-wrapper {
    width: 100%;
  }

  ${(props) => `${props.styles}`}
`;
